import React, { useMemo } from "react";
import { TableShapes } from "@repo/database";

import { cn } from "~/lib/utils";

type Theme = "light" | "dark";

interface TableComponentProps {
  reference: string;
  className?: string;
  rotation: number;
  theme?: Theme;
  width?: number;
  height?: number; // Optional height, defaults to 80
  numChairs?: number; // Specify the number of chairs
  shape?: TableShapes; // Specify the shape of the table
}

const measureTextWidth = (text: string, font: string): number => {
  if (typeof document === "undefined") {
    return text.length * 8;
  }

  const canvas: HTMLCanvasElement =
    measureTextWidth.canvas ??
    (measureTextWidth.canvas = document.createElement("canvas"));
  const context: CanvasRenderingContext2D | null = canvas.getContext("2d");

  if (context) {
    context.font = font;
    const metrics: TextMetrics = context.measureText(text);
    return metrics.width;
  }

  return text.length * 8;
};

measureTextWidth.canvas = null as HTMLCanvasElement | null;

const truncateTextWithEllipsis = (
  text: string,
  maxWidth: number,
  font: string,
): string => {
  if (measureTextWidth(text, font) <= maxWidth) {
    return text;
  }

  let truncated = text;
  while (
    measureTextWidth(`${truncated}...`, font) > maxWidth &&
    truncated.length > 0
  ) {
    truncated = truncated.slice(0, -1);
  }

  return truncated.length > 0 ? `${truncated}...` : "...";
};

export const TableComponent: React.FC<TableComponentProps> = ({
  reference,
  className = "",
  rotation,
  theme = "light",
  height = 80,
  width,
  numChairs = 2, // Default number of chairs to 2
  shape = TableShapes.square, // Default shape to square
}) => {
  const chairRadius = height * 0.166; // Chair radius based on height
  const padding = chairRadius * 1; // Padding based on chair size
  const chairSpacing = chairRadius * 2; // Space between chairs

  // Calculate the width dynamically based on the number of chairs
  width = useMemo(() => {
    if (shape === TableShapes.round) return height;

    // For square tables
    if (shape === TableShapes.square) {
      const maxChairsPerSide = 4; // Allow up to 4 chairs without increasing width
      if (numChairs <= maxChairsPerSide) {
        return height; // Keep width equal to height for up to 4 chairs
      }

      const additionalChairs = numChairs - maxChairsPerSide;
      const additionalWidth =
        additionalChairs * (chairRadius * 2 + chairSpacing);

      return height + additionalWidth;
    } else {
      const additionalWidth = chairRadius * 2 + chairSpacing;

      return (width ?? height) + additionalWidth;
    }
  }, [numChairs, chairRadius, chairSpacing, shape, height, width]);

  const fontSize = Math.min(width, height) * 0.2;

  const font = `bold ${fontSize}px sans-serif`;

  const displayText: string = useMemo(() => {
    const maxTextWidth = width - 40;
    return truncateTextWithEllipsis(reference, maxTextWidth, font);
  }, [reference, width, font]);

  const tableWidth = width - 2 * padding;
  const tableHeight = height - 2 * padding;
  const tableX = padding;
  const tableY = padding;

  // Calculate positions of chairs
  const chairs = useMemo(() => {
    const positions: { cx: number; cy: number }[] = [];

    if (shape === TableShapes.square) {
      if (numChairs === 4) {
        // Place one chair on each side for exactly 4 chairs
        positions.push({ cx: tableX + tableWidth / 2, cy: tableY }); // Top
        positions.push({
          cx: tableX + tableWidth / 2,
          cy: tableY + tableHeight,
        }); // Bottom
        positions.push({ cx: tableX, cy: tableY + tableHeight / 2 }); // Left
        positions.push({
          cx: tableX + tableWidth,
          cy: tableY + tableHeight / 2,
        }); // Right
      } else {
        const chairsPerSide = Math.floor(numChairs / 2);
        const horizontalChairSpacing = tableWidth / (chairsPerSide + 1);

        // Top and bottom chairs
        for (let i = 1; i <= chairsPerSide; i++) {
          positions.push({
            cx: tableX + i * horizontalChairSpacing,
            cy: tableY, // Top side
          });
          positions.push({
            cx: tableX + i * horizontalChairSpacing,
            cy: tableY + tableHeight, // Bottom side
          });
        }

        // Handle the remaining chair if the number is odd
        if (numChairs % 2 !== 0) {
          // Place the extra chair on the left side
          const verticalChairSpacing = tableHeight / 2;
          positions.push({
            cx: tableX, // Left side
            cy: tableY + verticalChairSpacing, // Middle of left side
          });
        }
      }
    } else if (shape === TableShapes.rectangle) {
      // For rectangle tables

      // Distribute chairs along longer sides first
      const chairsPerSide = Math.floor(numChairs / 2);
      const isWidthGreater = tableWidth >= tableHeight;
      const longerSideChairs = chairsPerSide;
      const shorterSideChairs = numChairs - longerSideChairs * 2;

      if (isWidthGreater) {
        // Longer sides are top and bottom
        const horizontalChairSpacing = tableWidth / (chairsPerSide + 1);
        for (let i = 1; i <= chairsPerSide; i++) {
          positions.push({
            cx: tableX + i * horizontalChairSpacing,
            cy: tableY, // Top side
          });
          positions.push({
            cx: tableX + i * horizontalChairSpacing,
            cy: tableY + tableHeight, // Bottom side
          });
        }

        // Place remaining chairs on the shorter sides (left and right)
        const verticalChairSpacing = tableHeight / (shorterSideChairs + 1);
        for (let i = 1; i <= shorterSideChairs; i++) {
          positions.push({
            cx: tableX, // Left side
            cy: tableY + i * verticalChairSpacing,
          });
          positions.push({
            cx: tableX + tableWidth, // Right side
            cy: tableY + i * verticalChairSpacing,
          });
        }
      } else {
        // Longer sides are left and right
        const verticalChairSpacing = tableHeight / (chairsPerSide + 1);
        for (let i = 1; i <= chairsPerSide; i++) {
          positions.push({
            cx: tableX, // Left side
            cy: tableY + i * verticalChairSpacing,
          });
          positions.push({
            cx: tableX + tableWidth, // Right side
            cy: tableY + i * verticalChairSpacing,
          });
        }

        // Place remaining chairs on the shorter sides (top and bottom)
        const horizontalChairSpacing = tableWidth / (shorterSideChairs + 1);
        for (let i = 1; i <= shorterSideChairs; i++) {
          positions.push({
            cx: tableX + i * horizontalChairSpacing,
            cy: tableY, // Top side
          });
          positions.push({
            cx: tableX + i * horizontalChairSpacing,
            cy: tableY + tableHeight, // Bottom side
          });
        }
      }
    } else {
      // Calculate for round shape
      const centerX = width / 2;
      const centerY = height / 2;
      const radius = Math.min(tableWidth, tableHeight) / 2 + chairRadius;

      for (let i = 0; i < numChairs; i++) {
        const angle = (i * 2 * Math.PI) / numChairs;
        const cx = centerX + (radius * Math.cos(angle)) / 1.5;
        const cy = centerY + (radius * Math.sin(angle)) / 1.5;
        positions.push({ cx, cy });
      }
    }
    return positions;
  }, [
    tableX,
    tableY,
    tableHeight,
    tableWidth,
    numChairs,
    shape,
    width,
    height,
    chairRadius,
  ]);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={`relative will-change-transform ${className}`}
      aria-labelledby="tableRectangleTitle"
      role="img"
    >
      <g>
        {chairs.map((chair, index) => (
          <circle
            key={index}
            cx={chair.cx}
            cy={chair.cy}
            r={chairRadius}
            className="fill-white stroke-[#171721] stroke-2"
          />
        ))}

        {shape === TableShapes.square || shape === TableShapes.rectangle ? (
          <rect
            className={cn("fill-white", className)}
            x={tableX}
            y={tableY}
            fill={theme === "dark" ? "#333" : "#f9f9f9"}
            stroke="#171721"
            strokeWidth="2"
            width={tableWidth}
            height={tableHeight}
            rx="3"
          />
        ) : (
          <circle
            cx={width / 2}
            cy={height / 2}
            r={Math.min(tableWidth, tableHeight) / 2}
            className={cn("fill-white", className)}
            fill={theme === "dark" ? "#333" : "#f9f9f9"}
            stroke="#171721"
            strokeWidth="2"
          />
        )}

        <text
          transform={`rotate(${-rotation}, ${width / 2}, ${height / 2})`} // Correct rotation around center
          x={width / 2}
          y={height / 2}
          dominantBaseline="middle"
          textAnchor="middle"
          fontFamily="sans-serif"
          fontSize={fontSize}
          fontWeight="bold"
          className="stroke-transparent fill-[#171721]"
        >
          {displayText}
        </text>
      </g>
    </svg>
  );
};
